import type { SideIncomeRuleSet } from "./types.js";

// side-income ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// Every constant below is transcribed from the verification ledgers with its
// source + the per-figure note the data-critic recorded:
//   • PAYG-instalment auto-entry $4,000 / $1,000 — side-income ledger Figs 6/7
//     (CITED, verbatim ATO wording).
//   • GDP adjustment factor 4% for FY2025-26 — side-income ledger Fig 8 (CITED).
//     Forward note: rises to 5% for FY2026-27 → that year needs its own table.
//   • GST $75k trip-wire + 21-day deadline — side-income Fig 9 / gst ledger
//     Figs 1/3 (CITED).
//   • GST rate 10% and the 1/11 inclusive divisor — gst ledger Fig 7 / rideshare
//     ledger Fig 7 (CITED).
//
// The marginal brackets, Medicare levy, and HELP bands that the set-aside
// stacking needs are NOT duplicated here — they live in the shared `income-tax`
// FY2025-26 ruleset, which `calculate.ts` resolves via `incomeTaxRulesFor`.
//
// Never edit a shipped ruleset to "fix" a later year — author a new fyXXXX-XX.ts
// and register it. The PAYG-instalment GDP factor and (legislated) bracket
// changes reprice; re-verify each FY rollover.
export const fy2025_26: SideIncomeRuleSet = {
  engine: "side-income",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    paygInstalments:
      "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/payg-instalments/starting-payg-instalments",
    gdpAdjustment:
      "https://softwaredevelopers.ato.gov.au/gdp-adjustment-2025-26-gst-and-payg-instalments",
    gstRegistration:
      "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/registering-for-gst",
    gstHowItWorks:
      "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/how-gst-works",
    rideSourcing:
      "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/sharing-economy-and-tax/ride-sourcing/registrations",
    serr: "https://www.ato.gov.au/businesses-and-organisations/preparing-lodging-and-paying/third-party-reporting/sharing-economy-reporting-regime/what-is-the-serr",
  },
  sourcesVerifiedAt: "2026-06-22",

  // PAYG-instalment auto-entry (side-income ledger Figs 6/7, CITED). The ATO
  // auto-enters an individual/sole trader when instalment income from the
  // latest return ≥ $4,000 AND tax payable on the latest NoA ≥ $1,000.
  paygInstalments: {
    instalmentIncomeThreshold: 4_000,
    taxOnNoaThreshold: 1_000,
    // GDP adjustment factor 4% for FY2025-26 (Fig 8, CITED). 5% for FY2026-27.
    gdpAdjustmentFactor: 0.04,
  },

  // GST registration (gst ledger Figs 1/3/7, CITED). $75k current-or-projected
  // rolling 12-month turnover; register within 21 days of meeting/expecting it;
  // 10% rate; GST = 1/11 of a GST-inclusive amount.
  gst: {
    turnoverThreshold: 75_000,
    registrationDeadlineDays: 21,
    gstRate: 0.1,
    gstInclusiveDivisor: 11,
  },
};
